// Watch mode's model (battle design 2.4), kept free of the DOM so it can be tested: the lead's move cooldowns as
// the client predicts them from MOVES and the hunt's `hit` events, and the plain words a log line uses for the
// element multiplier. Cooldowns belong to the creature, so they keep running through swaps and between kills.
import { MOVE } from '../shared/moves.ts';
import type { Element } from '../shared/elements.ts';

/** Element glyphs, 7×7 pixels, drawn in the element's color: the move bar's icons and a Signature's mark on the field. */
export const GLYPH: Record<Element, string[]> = {
  flame: ['...#...', '..##...', '..###.#', '.#####.', '##.####', '##..###', '.#####.'],
  tide: ['...#...', '...#...', '..###..', '.#####.', '.#.###.', '.##.##.', '..###..'],
  grove: ['....###', '..#####', '.####.#', '.###.##', '.#.####', '#.###..', '#......'],
  storm: ['...###.', '..###..', '.###...', '.#####.', '...###.', '..##...', '.#.....'],
  stone: ['.......', '..###..', '.##.##.', '######.', '###.###', '#######', '.#####.'],
  frost: ['...#...', '.#.#.#.', '..###..', '#######', '..###..', '.#.#.#.', '...#...'],
  shade: ['..####.', '.###...', '###....', '###....', '###....', '.###...', '..####.'],
  light: ['...#...', '.#...#.', '..###..', '#.###.#', '..###..', '.#...#.', '...#...'],
};

export interface Cooldown { start: number; end: number }

/** Per creature id, per move id: when the move was last cast and when it is ready again (ms, any one clock). */
export class MoveClock {
  private cds = new Map<number, Map<string, Cooldown>>();

  /** A move was cast now. `ms` is its cooldown for this creature. The basic attack has none and is ignored. */
  cast(creature: number, mv: string, now: number, ms: number) {
    const m = MOVE[mv];
    if (!m || m.slot === 'basic' || ms <= 0) return;
    let cd = this.cds.get(creature);
    if (!cd) this.cds.set(creature, (cd = new Map()));
    cd.set(mv, { start: now, end: now + ms });
    // Creatures that left the team long ago don't need their timers.
    if (this.cds.size > 50) this.cds.delete(this.cds.keys().next().value!);
  }

  /** The running cooldown of a move, or null when it is ready (or was never seen cast). */
  get(creature: number, mv: string, now: number): Cooldown | null {
    const c = this.cds.get(creature)?.get(mv);
    return c && c.end > now ? c : null;
  }

  /** How far the cooldown has run, 0 (just cast) to 1 (ready). */
  progress(creature: number, mv: string, now: number): number {
    const c = this.get(creature, mv, now);
    return c ? Math.max(0, Math.min(1, (now - c.start) / (c.end - c.start))) : 1;
  }

  clear() { this.cds.clear(); }
}

/**
 * The element multiplier in plain words: "very strong" (×3 or more, two elements hit hard), "strong", "weak",
 * "very weak", or '' when neutral. Never a number.
 */
export function effectWord(mult: number): string {
  if (!(mult > 0) || Math.abs(mult - 1) < 0.01) return '';
  if (mult >= 2.9) return 'very strong';
  if (mult > 1) return 'strong';
  if (mult <= 0.35) return 'very weak';
  return 'weak';
}
